<template>
  <div class="min-h-screen bg-gray-50" data-private :data-demo-record="demoRecordAttr(patientIdentifier)">
    <div class="bg-white border-b border-gray-200 px-6 py-4">
      <div class="flex items-center gap-3 text-xs">
        <router-link :to="`/patients/${patientIdentifier}`" class="text-gray-400 hover:text-gray-600">
          ← Patient
        </router-link>
        <span class="text-gray-300">·</span>
        <span class="font-semibold text-gray-900">Claims Explorer</span>
        <span class="font-mono text-gray-400 sensitive">{{ patientIdentifier }}</span>
      </div>
    </div>

    <div v-if="loading" class="flex items-center justify-center py-24">
      <div class="text-xs text-gray-400">Loading…</div>
    </div>

    <div v-else class="flex text-xs" style="height: calc(100vh - 57px);">
      <!-- Left: dates of service -->
      <div class="w-72 flex-shrink-0 border-r border-gray-200 bg-white overflow-y-auto">
        <div class="px-3 py-2 border-b border-gray-200 space-y-1">
          <div v-if="filterCodes" class="flex items-center gap-2 mb-1">
            <span class="text-blue-700 font-medium">Filtered to: {{ [...filterCodes].join(', ') }}</span>
            <button @click="clearCodeFilter" class="text-gray-400 hover:text-gray-600">Clear</button>
          </div>
          <label class="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" v-model="filterEhrOnly" class="rounded border-gray-300 text-blue-500 focus:ring-blue-500" />
            <span class="text-gray-600">Has encounter or claim</span>
          </label>
          <label v-if="assignedNpi" class="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" v-model="filterAssigned" class="rounded border-gray-300 text-blue-500 focus:ring-blue-500" />
            <span class="text-gray-600">Assigned provider only</span>
          </label>
          <div v-if="assignedNpi && filterAssigned" class="text-gray-400 pl-5 sensitive" data-spoof-kind="provider">{{ assignedName }}</div>
        </div>
        <div
          v-for="(group, idx) in dateGroups"
          :key="group.date"
          @click="selectedIdx = idx"
          class="px-3 py-2 cursor-pointer border-b border-gray-50 transition-colors"
          :class="selectedIdx === idx ? 'bg-blue-50 border-l-2 border-l-blue-500' : 'hover:bg-gray-50 border-l-2 border-l-transparent'"
        >
          <span class="font-medium text-gray-800 sensitive" data-spoof-kind="date">{{ group.date }}</span>
        </div>
      </div>

      <!-- Right: NPI-grouped cards for selected date -->
      <div class="flex-1 overflow-y-auto p-6">
        <template v-if="selectedGroup">
          <div class="space-y-4">
            <div
              v-for="group in npiGroups"
              :key="group.npi"
              class="border border-gray-200 rounded-lg bg-white"
            >
              <div class="px-4 py-3 border-b border-gray-100">
                <div class="font-semibold text-gray-900 sensitive">{{ group.name }}</div>
                <div class="font-mono text-gray-400">NPI <span class="sensitive">{{ group.npi }}</span></div>
              </div>

              <div class="p-4 space-y-3">
                <!-- EOB cards -->
                <div
                  v-for="eob in group.eobs"
                  :key="eob.CLM_ID"
                  class="border border-gray-100 rounded bg-gray-50 px-3 py-2"
                >
                  <div class="font-medium text-gray-400 uppercase mb-1">EOB</div>
                  <div class="text-gray-800 sensitive" data-spoof-kind="identifier">{{ eob.CLM_ID }}</div>
                  <div v-if="eobCptMap[eob.CLM_ID] && eobCptMap[eob.CLM_ID].length" class="flex flex-wrap gap-1 mt-1">
                    <span v-for="code in eobCptMap[eob.CLM_ID]" :key="code" class="font-mono px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">{{ code }}</span>
                  </div>
                </div>

                <!-- CLAIM cards -->
                <div
                  v-for="claim in group.claims"
                  :key="claim.CLAIM_ID"
                  class="border border-amber-200 rounded bg-amber-50 px-3 py-2"
                >
                  <div class="font-medium text-amber-600 uppercase mb-1">Claim</div>
                  <div class="text-gray-800 sensitive" data-spoof-kind="identifier">{{ claim.CLAIM_ID }}</div>
                </div>

                <!-- Encounter cards -->
                <div
                  v-for="enc in group.encounters"
                  :key="enc.ENCOUNTER_KEY"
                  class="border border-blue-200 rounded bg-blue-50 px-3 py-2"
                >
                  <div class="font-medium text-blue-600 uppercase mb-1">Encounter</div>
                  <div class="text-gray-800 sensitive" data-spoof-kind="identifier">{{ enc.ENCOUNTER_KEY }}</div>
                </div>

                <!-- Diagnosis flow -->
                <DxFlow
                  v-if="group.encDxCodes.length || group.clmDxCodes.length || group.eobDxCodes.length"
                  :encounter-codes="group.encDxCodes"
                  :claim-codes="group.clmDxCodes"
                  :eob-codes="group.eobDxCodes"
                  :condition-index="conditionIndex"
                  class="mt-3 border-t border-gray-100 pt-3"
                />
              </div>
            </div>
          </div>
        </template>
        <div v-else class="flex items-center justify-center h-full text-gray-400">
          Select a date
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { demoRecordAttr } from '@/utils/demoSpoof'
import DxFlow from './DxFlow.vue'

export default {
  name: 'Patient_Claims_Explorer',
  components: { DxFlow },
  props: {
    patientIdentifier: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      nodes: {},
      edges: [],
      selectedIdx: null,
      filterAssigned: false,
      filterEhrOnly: false,
    }
  },
  computed: {
    assignedNpi() {
      const assigned = (this.nodes.AssignedPractitioner || [])[0]
      return assigned?.NPI || null
    },
    assignedName() {
      const assigned = (this.nodes.AssignedPractitioner || [])[0]
      return assigned?.PROVIDER_NAME || this.assignedNpi
    },
    practitionerIndex() {
      const idx = {}
      for (const p of (this.nodes.Practitioner || [])) {
        idx[p.NPI] = p
      }
      return idx
    },
    // Resolve rendering NPI for each EOB from EOB_PRACTITIONER edges
    eobNpiMap() {
      const map = {}
      for (const c of (this.nodes.Claim || [])) {
        const edges = (this.edgeByFrom['EOB_PRACTITIONER'] || {})[c.CLM_ID] || []
        // Take first rendering practitioner
        map[c.CLM_ID] = edges.length ? edges[0].to : (c.FAC_PRVDR_NPI_NUM || '')
      }
      return map
    },
    dateGroups() {
      const byDate = {}
      const assigned = this.filterAssigned ? this.assignedNpi : null
      const codeFilter = this.filterCodes

      for (const c of (this.nodes.Claim || [])) {
        if (assigned && this.eobNpiMap[c.CLM_ID] !== assigned) continue
        // When code filter active, skip EOBs that don't have any matching code
        if (codeFilter) {
          const allCodes = this.eobAllCodesMap[c.CLM_ID] || []
          if (!allCodes.some(code => codeFilter.has(code))) continue
        }
        const dt = c.CLM_FROM_DT || ''
        if (!byDate[dt]) byDate[dt] = { date: dt, claims: [], encounters: [], hasEhr: false }
        byDate[dt].claims.push({ source: 'eob', raw: c })
      }

      // Pre-adj claims and encounters only included when no code filter
      if (!codeFilter) {
        for (const c of (this.nodes.PreAdjClaim || [])) {
          if (assigned && c.RENDERING_NPI !== assigned) continue
          const dt = c.SERVICE_DATE || ''
          if (!byDate[dt]) byDate[dt] = { date: dt, claims: [], encounters: [], hasEhr: false }
          byDate[dt].claims.push({ source: 'pre-adj', raw: c })
          byDate[dt].hasEhr = true
        }

        for (const e of (this.nodes.Encounter || [])) {
          const dt = e.ENCOUNTER_DATE || ''
          if (!byDate[dt]) byDate[dt] = { date: dt, claims: [], encounters: [], hasEhr: false }
          byDate[dt].encounters.push(e)
          byDate[dt].hasEhr = true
        }
      }

      let groups = Object.values(byDate)
      if (this.filterEhrOnly) groups = groups.filter(g => g.hasEhr)
      return groups.sort((a, b) => (b.date || '').localeCompare(a.date || ''))
    },
    selectedGroup() {
      if (this.selectedIdx == null) return null
      return this.dateGroups[this.selectedIdx] || null
    },
    edgeByFrom() {
      const byFrom = {}
      for (const e of this.edges) {
        ;(byFrom[e.type] ||= {})[e.from] ||= []
        byFrom[e.type][e.from].push(e)
      }
      return byFrom
    },
    encounterIndex() {
      const idx = {}
      for (const e of (this.nodes.Encounter || [])) {
        idx[e.ENCOUNTER_KEY] = e
      }
      return idx
    },
    conditionIndex() {
      const idx = {}
      for (const c of (this.nodes.Condition || [])) {
        idx[c.CODE] = c
      }
      return idx
    },
    // Collect HCPCS codes per EOB from EOB_ITEM edges → Item nodes
    eobCptMap() {
      const map = {}
      for (const c of (this.nodes.Claim || [])) {
        const codes = []
        for (const e of ((this.edgeByFrom['EOB_ITEM'] || {})[c.CLM_ID] || [])) {
          const item = this.itemIndex[e.to]
          if (item && item.CODE && item.CODE_TYPE === 'HCPCS') {
            if (!codes.includes(item.CODE)) codes.push(item.CODE)
          }
        }
        map[c.CLM_ID] = codes
      }
      return map
    },
    // Collect rev center codes per EOB from ITEM_REV_CTR edges
    eobRevMap() {
      const map = {}
      for (const c of (this.nodes.Claim || [])) {
        const codes = []
        for (const itemEdge of ((this.edgeByFrom['EOB_ITEM'] || {})[c.CLM_ID] || [])) {
          for (const revEdge of ((this.edgeByFrom['ITEM_REV_CTR'] || {})[itemEdge.to] || [])) {
            if (revEdge.to && !codes.includes(revEdge.to)) codes.push(revEdge.to)
          }
        }
        map[c.CLM_ID] = codes
      }
      return map
    },
    // All codes (HCPCS + rev center) per EOB — for filtering
    eobAllCodesMap() {
      const map = {}
      for (const c of (this.nodes.Claim || [])) {
        map[c.CLM_ID] = [...(this.eobCptMap[c.CLM_ID] || []), ...(this.eobRevMap[c.CLM_ID] || [])]
      }
      return map
    },
    // HCPCS code index for description-based matching
    hcpcsIndex() {
      const idx = {}
      for (const h of (this.nodes.HCPCS_CODE || [])) {
        if (h.CODE) idx[h.CODE] = h
      }
      return idx
    },
    itemIndex() {
      const idx = {}
      for (const item of (this.nodes.Item || [])) {
        idx[item._key || item.ITEM_ID] = item
      }
      return idx
    },
    filterCodes() {
      const codes = this.$route.query.codes
      if (!codes) return null
      return new Set(codes.split(','))
    },
    npiGroups() {
      if (!this.selectedGroup) return []
      const byNpi = {}

      const ensure = (npi) => {
        if (!byNpi[npi]) {
          const prac = this.practitionerIndex[npi] || {}
          byNpi[npi] = {
            npi: npi === '_unknown' ? 'Unknown' : npi,
            name: prac.PROVIDER_NAME || (npi === '_unknown' ? 'Unknown Provider' : npi),
            eobs: [],
            claims: [],
            encounters: [],
            encDxCodes: new Set(),
            clmDxCodes: new Set(),
            eobDxCodes: new Set(),
          }
        }
        return byNpi[npi]
      }

      // EOBs → group by rendering NPI from EOB_PRACTITIONER edges
      for (const entry of this.selectedGroup.claims) {
        if (entry.source !== 'eob') continue
        const c = entry.raw
        const npi = this.eobNpiMap[c.CLM_ID] || '_unknown'
        const g = ensure(npi)
        g.eobs.push(c)
        for (const e of ((this.edgeByFrom['EOB_CONDITION'] || {})[c.CLM_ID] || [])) {
          g.eobDxCodes.add(e.to)
        }
      }

      // Pre-adj claims → group by RENDERING_NPI
      for (const entry of this.selectedGroup.claims) {
        if (entry.source !== 'pre-adj') continue
        const c = entry.raw
        const g = ensure(c.RENDERING_NPI || '_unknown')
        g.claims.push(c)
        for (const e of ((this.edgeByFrom['CLAIM_CONDITION'] || {})[c.CLAIM_ID] || [])) {
          g.clmDxCodes.add(e.to)
        }
      }

      // Encounters → use NPI from encounter node, or from linked claim
      const encKeyToNpi = {}
      for (const entry of this.selectedGroup.claims) {
        if (entry.source !== 'pre-adj') continue
        const npi = entry.raw.RENDERING_NPI || ''
        const encEdges = (this.edgeByFrom['CLAIM_ENCOUNTER'] || {})[entry.raw.CLAIM_ID] || []
        for (const ee of encEdges) {
          if (npi) encKeyToNpi[ee.to] = npi
        }
      }

      const seenEnc = new Set()
      for (const enc of (this.selectedGroup.encounters || [])) {
        const key = enc.ENCOUNTER_KEY || ''
        if (!key || seenEnc.has(key)) continue
        seenEnc.add(key)
        const npi = encKeyToNpi[key] || enc.NPI || '_unknown'
        const g = ensure(npi)
        g.encounters.push(enc)
        for (const e of ((this.edgeByFrom['ENCOUNTER_CONDITION'] || {})[key] || [])) {
          g.encDxCodes.add(e.to)
        }
      }

      // Convert Sets to Arrays for props
      for (const g of Object.values(byNpi)) {
        g.encDxCodes = [...g.encDxCodes]
        g.clmDxCodes = [...g.clmDxCodes]
        g.eobDxCodes = [...g.eobDxCodes]
      }

      return Object.values(byNpi)
    },
  },
  watch: {
    filterAssigned() {
      this.selectedIdx = null
    },
    filterEhrOnly() {
      this.selectedIdx = null
    },
  },
  mounted() {
    this.fetchSubgraph()
  },
  methods: {
    // Demo Mode: dates tagged data-spoof-kind="date" inside this record
    // share one offset (an opaque key; nothing is bound outside Demo Mode).
    demoRecordAttr,
    clearCodeFilter() {
      const q = { ...this.$route.query }
      delete q.codes
      this.$router.replace({ query: q })
    },
    async fetchSubgraph() {
      this.loading = true
      const { data } = await axios.get(`/api/graph/patient/${this.patientIdentifier}/subgraph/`)
      this.nodes = data.nodes || {}
      this.edges = data.edges || []
      this.loading = false
      this.$nextTick(() => {
        const date = this.$route.query.date
        if (date) {
          const idx = this.dateGroups.findIndex(g => g.date === date)
          if (idx >= 0) this.selectedIdx = idx
        }
      })
    },
  },
}
</script>
